import { useCallback, useEffect, useMemo, useState } from "react";

import { ReactIcons, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";
import { FullscreenContainer } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { focusIconForLayout, NewTriLayoutContainer, NewTriLayoutContainerProps } from "../../../../../components/player/container/NewTriLayoutContainer";
import { PlayerItem, PlayerRendererProvider } from "../../../../../components/player/interfaces";
import { MasterPlayerWorkBox } from "../../../../../components/workBoxes/MasterPlayerWorkBox";
import { PlayerActionItem } from "../../../../../components/workBoxes/PlayerWorkBox";
import { LayoutType } from "../../../../../interfaces/ui";
import { useAppStoreSelector } from "../../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../../contexts/sdk/context";
import { isExpert } from "../../../../../helpers/roles";
import { useBoardMedia } from "../../../../../hooks/board";
import { useDeviceControlsContext } from "../../../../../contexts/deviceControls/context";
import { pushDeviceControlsCmd } from "../../../../../hooks/deviceControls";
import { useVideoSubscriptions } from "../../../../../hooks/ovenmedia";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useMeetingUtilsContext } from "../../../../../contexts/meetingUtils/context";

import "./dashboard.css";
import styles from "./dashboard.module.css";

export const MeetingDashboardStudentPage = () => {
  const sdkCtx = useSdkContext();

  const { containerAPI } = useMeetingUtilsContext();
  const { processAction } = useDeviceControlsContext();

  const [meeting] = useMeetingSelector(containerAPI, "meeting");
  const [sessions] = useMeetingSelector(containerAPI, "sessions");
  const [ownSession] = useMeetingSelector(containerAPI, "session");

  const layout = useAppStoreSelector(state => state.layout);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);

  const subscribptionsAPI = useVideoSubscriptions();

  const boardLocalOrRemoteMedia = useBoardMedia();

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));

    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  const playerItems = useMemo(() => enabledMedia?.map(
    (media) =>
      new PlayerItem(media.type === "local" ? {
        type: "stream",
        mediaStream: media.stream,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      } : {
        type: "video_session",
        videoSession: media.videoSession,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      })
  ), [enabledMedia]);

  // Per-item fullscreen state. In dashboard the wrapper goes around each
  // workbox individually (unlike VOD where it wraps the whole multiplayer),
  // so a single id is enough — only one item can be fullscreen at a time.
  const [fullscreenItemId, setFullscreenItemId] = useState<string | undefined>();

  const onFullscreenChange = useCallback((enabled: boolean) => {
    if (!enabled) setFullscreenItemId(undefined);
  }, []);

  const renderPlayer = useCallback((
    item: PlayerItem,
    rendererProvider: PlayerRendererProvider,
    session: MeetingSession,
    mediaSession: MeetingSessionMedia,
    isFocused: boolean,
    isSmall: boolean,
    onFocus: () => void,
  ) => {
    if (!meeting || !containerAPI) return null;

    const id = item.getId();
    const isFullscreen = fullscreenItemId === id;

    const hasControls = !!mediaSession.extra?.controls && isExpert(ownSession);
    const deviceId = mediaSession.deviceId;
    const roomId = meeting.room?.id;
    const isLocal = session.id === ownSession?.id;

    const sendControl = (action: "move" | "stopMove" | "zoom" | "stopZoom", content: any) => {
      if (!deviceId) return;
      if (isLocal) {
        processAction(deviceId, action, content);
      } else {
        if (!roomId) return;
        pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, action, content);
      }
    };

    const overlayContent = hasControls ? (
      <VideoControlsOverlay
        onUp={(on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "up" })}
        onDown={(on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "down" })}
        onLeft={(on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "left" })}
        onRight={(on: boolean) => sendControl(on ? "move" : "stopMove", { direction: "right" })}
        onZoomIn={(on: boolean) => sendControl(on ? "zoom" : "stopZoom", { direction: "in" })}
        onZoomOut={(on: boolean) => sendControl(on ? "zoom" : "stopZoom", { direction: "out" })}
      >
        <></>
      </VideoControlsOverlay>
    ) : undefined;

    // Focus stays a centered hover overlay (only meaningful in split layouts).
    const overlayActions: PlayerActionItem[] = [];
    if (!isFullscreen && !isFocused && layout !== LayoutType.Full) {
      const FocusIcon = focusIconForLayout(layout);
      overlayActions.push({ key: "focus", icon: <FocusIcon />, onClick: onFocus, label: "Mettre en avant" });
    }

    // Fullscreen toggle lives in the topbar.
    const topbarActions: PlayerActionItem[] = [];
    if (!isFullscreen) {
      topbarActions.push({
        key: "fullscreen",
        icon: <ReactIcons.Expand />,
        onClick: () => setFullscreenItemId(id),
        label: "Plein écran",
      });
    } else {
      topbarActions.push({
        key: "exit-fullscreen",
        icon: <ReactIcons.Shrink />,
        onClick: () => setFullscreenItemId(undefined),
        label: "Quitter le plein écran",
      });
    }

    return rendererProvider(
      (children, renderingCtx) => {
        return (
          <FullscreenContainer
            useNative
            fullscreen={isFullscreen}
            onFullScreen={onFullscreenChange}
            style={{ width: "100%", height: "100%" }}
          >
            <MasterPlayerWorkBox
              meetingAPI={containerAPI}
              session={session}
              mediaSession={mediaSession}
              overlayMode={"overlay-hoverable"}
              overlayContent={overlayContent}
              topbarActions={topbarActions}
              overlayActions={overlayActions}
              isSmall={isSmall}
              isPlayingSound={renderingCtx?.isPlayingSound}
              mediaButtons
            >
              {children}
            </MasterPlayerWorkBox>
          </FullscreenContainer>
        );
      }
    );
  }, [meeting, containerAPI, ownSession, processAction, sdkCtx.client, layout, fullscreenItemId, onFullscreenChange]);

  const triLayoutRenderItem = useCallback<NonNullable<NewTriLayoutContainerProps["renderItem"]>>((
    rendererProvider,
    { item, isFocused, isFullscreen, onFocus },
  ) => {
    const itemData = item.getData();
    const session = itemData.extra?.meetingSession;
    const mediaSession = itemData.extra?.mediaSession;
    if (!session || !mediaSession) return undefined;
    return renderPlayer(item, rendererProvider, session, mediaSession, isFocused, !isFocused && !isFullscreen, onFocus);
  }, [renderPlayer]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const remoteVideoSessions = enabledMedia
      ?.filter((media) => media.type === "remote")
      .map((media) => (media as any).videoSession);
    subscribptionsAPI.update(remoteVideoSessions.map((media) => ({ session: media, active: true })));
    return () => subscribptionsAPI.update(remoteVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledMedia]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return null;
  if (!containerAPI) return null;

  const renderNewLayoutContent = () => {
    if (!playerItems?.length) {
      return (
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed var(--color-element-text)",
          borderRadius: 20,
        }}>
          <div style={{ color: "var(--color-element-text)" }}>{boardLocalOrRemoteMedia.length ? "Aucun flux sélectionné" : "Aucun flux réceptionné"}</div>
        </div>
      );
    }
    return (
      <NewTriLayoutContainer
        meeting={meeting}
        sessions={sessions ?? []}
        items={playerItems}
        layout={layout}
        renderItem={triLayoutRenderItem}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {/* {renderFocusContent()} */}
    </div>
  );
};
